import { motion } from "framer-motion";
import { Lightbulb, Paintbrush, Wrench, ArrowRight } from "lucide-react";

export const WorkProcess = () => {
  const steps = [
    {
      id: 1,
      title: "Ideate",
      icon: Lightbulb,
      iconBg: "bg-amber-500/20",
      iconColor: "text-amber-500",
      description: "Where ideas begin and visions take shape.",
      content: "We dive deep into your world — understanding your goals, audience, and aspirations. Together, we define the core problem, explore fresh concepts, and align on a clear direction.",
      items: [
        "Discovery sessions / workshops",
        "Brand or product strategy",
        "User research",
        "Moodboards & inspiration",
        "Creative briefs"
      ]
    },
    {
      id: 2,
      title: "Design",
      icon: Paintbrush,
      iconBg: "bg-coral/20",
      iconColor: "text-coral",
      description: "Where form meets function.",
      content: "We translate insights into purposeful design — striking visuals, intuitive user flows, and delightful interactions. This is where ideas take visual form, grounded in UX best practices and clean aesthetics.",
      items: [
        "Wireframes & user flows",
        "UI/UX design",
        "Brand identity or visual systems",
        "Interactive prototypes",
        "Design systems & accessibility review"
      ]
    },
    {
      id: 3,
      title: "Build",
      icon: Wrench,
      iconBg: "bg-blue-500/20",
      iconColor: "text-blue-500",
      description: "Where vision becomes reality.",
      content: "Our team brings the designs to life with clean, scalable code and robust digital architecture. We make sure everything works beautifully — across screens, platforms, and use cases.",
      items: [
        "Frontend & backend development",
        "Cross-platform app development (Flutter, Web)",
        "CMS & API integration",
        "Testing & QA",
        "Launch support & handover"
      ]
    }
  ];

  return (
    <section className="py-20 px-4 bg-background">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-5xl font-bold mb-4">Our Creative Process</h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            We've refined our approach to deliver exceptional results through a clear, collaborative process.
          </p>
        </motion.div>

        {/* Process Steps Visual Flow - Desktop */}
        <div className="hidden md:flex justify-center items-center mb-16">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="flex flex-col items-center"
              >
                <div className={`w-20 h-20 rounded-full ${step.iconBg} flex items-center justify-center mb-4`}>
                  <step.icon className={`w-10 h-10 ${step.iconColor}`} />
                </div>
                <h3 className="text-xl font-bold">{step.title}</h3>
              </motion.div>
              
              {index < steps.length - 1 && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.2 + 0.3 }}
                  className="mx-6 flex items-center"
                >
                  <div className="w-24 h-[2px] bg-gradient-to-r from-coral to-coral/50"></div>
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.2 + 0.6 }}
                  >
                    <ArrowRight className="w-8 h-8 text-coral" />
                  </motion.div>
                  <div className="w-24 h-[2px] bg-gradient-to-r from-coral/50 to-coral/20"></div>
                </motion.div>
              )}
            </div>
          ))}
        </div>

        {/* Process Steps Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
          {steps.map((step, index) => (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="bg-accent-navy/20 rounded-xl p-6 md:p-8 border border-gray-800 relative group hover:bg-accent-navy/30 transition-all duration-300"
            >
              {/* Step Number - Mobile Only */}
              <div className="md:hidden flex items-center mb-4">
                <div className={`w-12 h-12 rounded-full ${step.iconBg} flex items-center justify-center mr-4`}>
                  <step.icon className={`w-6 h-6 ${step.iconColor}`} />
                </div>
                <h3 className="text-2xl font-bold">{step.id === 2 ? `🎨 ${step.id}. ${step.title}` : step.id === 3 ? `🛠 ${step.id}. ${step.title}` : `${step.id}. ${step.title}`}</h3>
              </div>

              {/* Step Number - Desktop */}
              <div className="hidden md:block">
                <h3 className="text-2xl font-bold mb-2">{step.id === 2 ? `🎨 ${step.title}` : step.id === 3 ? `🛠 ${step.title}` : step.title}</h3>
              </div>

              <p className="text-lg font-medium text-coral mb-3">{step.description}</p>
              <p className="text-muted-foreground text-white">{step.content}</p>
              <br />
              <div className="mb-4">
                <h4 className="text-sm font-medium mb-2 text-coral"> What happens here:</h4>
                <ul className="space-y-2">
                  {step.items.map((item, i) => (
                    <li key={i} className="text-sm text-muted-foreground flex items-start">
                      <span className="mr-2">-</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {index < steps.length - 1 && (
                <div className="md:hidden flex justify-center mt-6">
                  <div className="flex flex-col items-center">
                    <div className="w-[2px] h-6 bg-gradient-to-b from-coral to-coral/50"></div>
                    <ArrowRight className="w-6 h-6 text-coral rotate-90 my-1" />
                    <div className="w-[2px] h-6 bg-gradient-to-b from-coral/50 to-coral/20"></div>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}; 